import FilterAlt from '@mui/icons-material/FilterAlt';
import StarIcon from '@mui/icons-material/Star';
import { Divider, Grid, Paper, Tooltip } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { isEqual } from 'lodash';
import { useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
    CHANNEL_TYPE_LABELS,
    INBOX_DATE_RANGE_LABELS,
    INBOX_DEFAULT_SORT,
    INBOX_FILTER_INITIAL_STATE,
    OWNERSHIP_LABELS,
} from '../../../sic-lib/defaults';
import { InboxFilter } from '../../../sic-lib/types/InboxTypes';
import { useAppSelector } from '../../../store/hooks';
import NewRecentDescription from '../NewRecentDescription/NewRecentDescription';
import NewRecentDirectionButtons from '../NewRecentDirectionButtons/NewRecentDirectionButtons';
import NewRecentFilterDrawer from '../NewRecentFilterDrawer/NewRecentFilterDrawer';
import { NewRecentSavedFiltersDrawer } from '../NewRecentFilterDrawer/NewRecentSavedFiltersDrawer';
import FilterDropDown from './FilterDropDown';

interface Props {
    currentFilter: InboxFilter;
    savedFilters: InboxFilter[] | undefined;
}

export default function NewRecentInboxFiltering({ currentFilter, savedFilters }: Props) {
    const { classes } = useStyles();
    const defaultFilter = useAppSelector(state => state.user.inboxFilter);
    const [showFilterDrawer, setShowFilterDrawer] = useState<boolean>(false);
    const [showSavedFiltersDrawer, setShowSavedFiltersDrawer] = useState<boolean>(false);
    const appliedFilterFlags = useMemo(() => {
        const isDefault = isEqual(defaultFilter, currentFilter);
        const isCurrentInitialState = isEqual(currentFilter, INBOX_FILTER_INITIAL_STATE);
        const isCurrentDirty =
            currentFilter.id === null
                ? !isCurrentInitialState
                : !isEqual(
                      currentFilter,
                      savedFilters?.find(itm => itm.id === currentFilter.id),
                  );

        return {
            isDefault,
            isCurrentInitialState,
            isCurrentDirty,
        };
    }, [currentFilter, defaultFilter, savedFilters]);
    // Filter icon drop down
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const openDropDownMenu = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    return (
        <>
            <Paper elevation={0} variant="outlined" className={classes.root}>
                <Grid
                    container
                    direction="row"
                    alignItems="center"
                    className={classes.gridContainer}
                    columnSpacing={1}
                >
                    <Grid item className={classes.directionBtns}>
                        <NewRecentDirectionButtons />
                    </Grid>

                    <Grid item>
                        <Divider
                            orientation="vertical"
                            light={true}
                            flexItem={true}
                            style={{ height: 44 }}
                        />
                    </Grid>

                    <Grid
                        item
                        onClick={() => setShowFilterDrawer(true)}
                        className={classes.openFiltersBtn}
                    >
                        {currentFilter.name ? `Filter: ${currentFilter.name}` : 'Search & Filter'}
                    </Grid>
                    <Grid item className={classes.filterIconBtnGridWrapper}>
                        <Tooltip
                            placement="top-start"
                            title={`Activity Date: ${
                                INBOX_DATE_RANGE_LABELS[currentFilter.filter.activityRange]
                            }, Registration Date: ${
                                INBOX_DATE_RANGE_LABELS[currentFilter.filter.registrationRange]
                            }, Channel Date: ${
                                INBOX_DATE_RANGE_LABELS[currentFilter.filter.channelsRange]
                            }, Channels: ${currentFilter.filter.channels
                                .map(itm => CHANNEL_TYPE_LABELS[itm])
                                .join(', ')}, Sort Order: ${
                                INBOX_DEFAULT_SORT.find(
                                    itm =>
                                        itm.sortItem.field === currentFilter.filter.sort.field &&
                                        itm.sortItem.direction ===
                                            currentFilter.filter.sort.direction,
                                )?.label
                            }, Ownership Scope: ${
                                OWNERSHIP_LABELS[currentFilter.filter.ownershipScope]
                            }`}
                        >
                            <span
                                id="dropdown-button"
                                aria-controls={openDropDownMenu ? 'dropdown-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={openDropDownMenu ? 'true' : undefined}
                                onClick={handleClick}
                                className={classes.filterIconBtn}
                            >
                                {appliedFilterFlags.isDefault &&
                                    !appliedFilterFlags.isCurrentDirty && (
                                        <StarIcon
                                            sx={{
                                                height: '100%',
                                                fontSize: '1.25rem',
                                                color: 'gold',
                                            }}
                                            className={classes.starIcon}
                                        />
                                    )}
                                {appliedFilterFlags.isCurrentDirty && (
                                    <FilterAlt
                                        sx={{
                                            height: '100%',
                                            fontSize: '1.25rem',
                                        }}
                                        className={classes.filterIconRed}
                                    />
                                )}
                                {!appliedFilterFlags.isDefault &&
                                    !appliedFilterFlags.isCurrentDirty && (
                                        <FilterAlt
                                            sx={{
                                                height: '100%',
                                                fontSize: '1.25rem',
                                            }}
                                            className={classes.filterIcon}
                                        />
                                    )}
                            </span>
                        </Tooltip>
                        <Grid item>
                            <NewRecentDescription />
                        </Grid>
                        <FilterDropDown
                            userDefaultFilter={defaultFilter}
                            openDropDownMenu={openDropDownMenu}
                            anchorEl={anchorEl}
                            setAnchorEl={setAnchorEl}
                            setShowFilterDrawer={setShowFilterDrawer}
                            setShowSavedFiltersDrawer={setShowSavedFiltersDrawer}
                        />
                    </Grid>
                </Grid>
            </Paper>
            <NewRecentFilterDrawer
                currentInboxFilter={currentFilter}
                isOpen={showFilterDrawer}
                setShowFilterDrawer={setShowFilterDrawer}
                setShowSavedFiltersDrawer={setShowSavedFiltersDrawer}
                appliedFilterFlags={appliedFilterFlags}
            />
            <NewRecentSavedFiltersDrawer
                isOpen={showSavedFiltersDrawer}
                currentInboxFilter={currentFilter}
                savedFilters={savedFilters}
                setShowFilterDrawer={setShowFilterDrawer}
                setShowSavedFiltersDrawer={setShowSavedFiltersDrawer}
            />
        </>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    root: {
        width: `calc(100% - ${theme.spacing(0.75)})`,
        padding: theme.spacing(0.25),
    },
    gridContainer: {
        width: '100%',
        maxWidth: '100%',
        marginLeft: 0,
    },
    directionBtns: {
        flex: 1,
        flexGrow: 0,
    },
    openFiltersBtn: {
        flex: 9,
        cursor: 'pointer',
        // border: "1px solid black",
        color: 'black',
    },
    filterIconBtnGridWrapper: {
        flex: 1,
        height: '44px', //sets container height
        marginRight: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
    },
    filterIconBtn: {
        display: 'flex',
        alignItems: 'center',
    },
    starIcon: {
        cursor: 'pointer',
    },
    filterIconRed: {
        color: 'rgba(255, 0, 0, 0.65)',
        cursor: 'pointer',
    },
    filterIcon: {
        color: 'rgba(0, 0, 0, 0.65)',
        cursor: 'pointer',
    },
}));
